import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { portalApi, formatApiError } from '../../services/api';
import { Timer, Receipt, Warning, Ticket, FileText, CurrencyEur } from '@phosphor-icons/react';
import { toast } from 'sonner';

const fmtEUR = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));

const KpiCard = ({ icon: Icon, label, value, color, testId, to }) => {
  const inner = (
    <div className={`card-swiss p-5 ${to ? 'hover:shadow-md transition' : ''}`} data-testid={testId}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-9 h-9 rounded ${color} flex items-center justify-center`}>
          <Icon weight="bold" size={18} />
        </div>
        <span className="overline">{label}</span>
      </div>
      <p className="text-3xl font-bold text-[#111418]">{value}</p>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
};

export default function PortalDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await portalApi.dashboard();
        setData(r.data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-6"><div className="card-swiss h-40 skeleton" /></div>;
  if (!data) return <div className="p-6">Keine Daten.</div>;

  const c = data.counts || {};
  const cust = data.customer || {};
  const isBiz = cust.customer_type === 'business' || cust.company_name;
  const name = isBiz ? cust.company_name : `${cust.first_name || ''} ${cust.last_name || ''}`.trim();

  return (
    <div className="p-6 space-y-6" data-testid="portal-dashboard-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-[#111418]">Willkommen, {name || cust.email}!</h1>
        <p className="text-[#525866] mt-1">Ihre Übersicht auf einen Blick.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard testId="kpi-active-lt" icon={Timer} label="Langzeit-Verträge" value={c.active_long_term_contracts} color="bg-indigo-50 text-indigo-600" to="/portal/long-term" />
        <KpiCard testId="kpi-active-bookings" icon={FileText} label="Aktive Buchungen" value={c.active_bookings} color="bg-blue-50 text-blue-600" to="/portal/bookings" />
        <KpiCard testId="kpi-open-invoices" icon={Receipt} label="Offene Rechnungen" value={c.open_invoices} color="bg-amber-50 text-amber-600" to="/portal/invoices" />
        <KpiCard testId="kpi-open-damages" icon={Warning} label="Offene Schäden" value={c.open_damages} color="bg-red-50 text-red-600" to="/portal/damages" />
        <KpiCard testId="kpi-open-tickets" icon={Ticket} label="Offene Strafzettel" value={c.open_tickets} color="bg-fuchsia-50 text-fuchsia-600" to="/portal/tickets" />
      </div>

      {/* Recent invoices */}
      <div className="card-swiss overflow-hidden" data-testid="recent-invoices-section">
        <div className="p-5 border-b border-[#E2E4E9]">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-[#111418] flex items-center gap-2">
            <Receipt weight="bold" size={16} /> Letzte Rechnungen
          </h2>
        </div>
        {data.recent_invoices?.length ? (
          <table className="table-swiss">
            <thead><tr><th>Rechnung</th><th>Datum</th><th className="text-right">Betrag</th><th>Status</th></tr></thead>
            <tbody>
              {data.recent_invoices.map((inv) => (
                <tr key={inv.id} data-testid={`recent-invoice-${inv.id}`}>
                  <td className="font-medium"><Link to="/portal/invoices" className="hover:underline">{inv.invoice_number}</Link></td>
                  <td className="text-sm text-[#525866]">{(inv.created_at || '').slice(0, 10)}</td>
                  <td className="text-right font-semibold">{fmtEUR(inv.total)}</td>
                  <td><span className={`text-xs px-2 py-1 rounded font-semibold ${inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : inv.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{inv.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-sm text-[#8A94A6]">Noch keine Rechnungen vorhanden.</div>
        )}
      </div>
    </div>
  );
}
